import { useState } from "react";
import { Box, Chip, Stack, Typography } from "@mui/joy";
import { AnimatePresence, motion } from "framer-motion";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";

import { BlueprintGrid, COLORS, Container, Section, SectionHeader } from "../shared/ui";

const LAYERS = [
  {
    id: "walls",
    title: "Геометрия стен",
    desc: "Контур помещений, толщина стен, дверные и оконные проёмы",
    included: true,
    default: true,
  },
  {
    id: "dimensions",
    title: "Размерные линии",
    desc: "Все размеры с шагом в миллиметрах, включая диагонали",
    included: true,
    default: true,
  },
  {
    id: "heights",
    title: "Высотные отметки",
    desc: "Высоты потолков, перепады пола, уровни ниш",
    included: true,
    default: false,
  },
  {
    id: "plumbing",
    title: "Сантехника",
    desc: "Привязки канализации, водоснабжения, приборов",
    included: true,
    default: false,
  },
  {
    id: "electricity",
    title: "Электрика",
    desc: "Розетки, выключатели, щиток, автоматы, выводы",
    included: false,
    default: false,
    extra: "+25 ₽/м²",
  },
  {
    id: "photo",
    title: "Фото и видео",
    desc: "Фотоотчёт каждой стены и всех узлов, доступ через Яндекс.Диск",
    included: true,
    default: false,
  },
  {
    id: "facade",
    title: "Фасад",
    desc: "Обмер и чертёж фасада здания с привязками витрин и входной группы",
    included: false,
    default: false,
    extra: "+30 ₽/м²",
  },
];

const VIEW_PLAN = "plan";
const VIEW_FACADE = "facade";

const VIDEO_POINTS = [
  { x: 105, y: 124, angle: -10 },
  { x: 191, y: 116, angle: 18 },
  { x: 255, y: 180, angle: 140 },
  { x: 338, y: 118, angle: 34 },
  { x: 392, y: 128, angle: 155 },
  { x: 125, y: 250, angle: -40 },
  { x: 205, y: 343, angle: 22 },
  { x: 115, y: 458, angle: -12 },
  { x: 236, y: 510, angle: 158 },
  { x: 306, y: 529, angle: -84 },
  { x: 68, y: 523, angle: 20 },
];

const SOCKETS = [
  { x: 91, y: 142 },
  { x: 263, y: 112 },
  { x: 391, y: 143 },
  { x: 111, y: 315 },
  { x: 122, y: 478 },
  { x: 224, y: 529 },
  { x: 317, y: 502 },
];

export default function WhatIncluded() {
  const [active, setActive] = useState(() => new Set(LAYERS.filter((layer) => layer.default).map((layer) => layer.id)));
  const [view, setView] = useState(VIEW_PLAN);

  const toggleLayer = (id) => {
    const willEnable = !active.has(id);

    setActive((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

    if (id === "facade") {
      setView(willEnable ? VIEW_FACADE : VIEW_PLAN);
    }
  };

  const selectView = (nextView) => {
    setView(nextView);

    if (nextView === VIEW_FACADE && !active.has("facade")) {
      setActive((prev) => new Set([...prev, "facade"]));
    }
  };

  return (
    <Section id="what-included" py={{ xs: 9, md: 13 }} sx={{ background: COLORS.bgLight }}>
      <BlueprintGrid opacity={0.045} size={28} />

      <Container maxWidth={1180}>
        <SectionHeader
          eyebrow="Что входит в базу"
          title="Соберите свой пакет замера"
          subtitle="Кликайте по слоям — увидите, как меняется ваш чертёж и что именно вы получите по итогу"
        />

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", lg: "0.96fr 1.04fr" },
            gap: { xs: 4, md: 5 },
            alignItems: "start",
          }}
        >
          <Box>
            <Stack spacing={1.4}>
              {LAYERS.map((layer, index) => (
                <LayerRow
                  key={layer.id}
                  layer={layer}
                  active={active.has(layer.id)}
                  onToggle={() => toggleLayer(layer.id)}
                  index={index}
                />
              ))}
            </Stack>

            <Box
              sx={{
                mt: 2.5,
                p: 2.25,
                borderRadius: "14px",
                border: "1px solid rgba(99,102,241,0.18)",
                background: "linear-gradient(135deg, rgba(99,102,241,0.08), rgba(16,185,129,0.04))",
              }}
            >
              <Typography level="body-sm" sx={{ color: COLORS.dark, fontWeight: 700, mb: 0.4 }}>
                {active.size === 0
                  ? "Выберите хотя бы один слой"
                  : `Вы включили ${active.size} ${declOfNum(active.size, ["слой", "слоя", "слоёв"])}`}
              </Typography>
              <Typography level="body-xs" sx={{ color: "neutral.600", lineHeight: 1.55 }}>
                В финальном пакете менеджер, обмерщик и чертёжник видят один и тот же состав данных.
              </Typography>
            </Box>
          </Box>

          <DrawingPreview active={active} view={view} onSelectView={selectView} />
        </Box>
      </Container>
    </Section>
  );
}

function LayerRow({ layer, active, onToggle, index }) {
  return (
    <motion.div
      initial={{ opacity: 0, x: -14 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.35, delay: index * 0.05 }}
    >
      <Box
        role="button"
        tabIndex={0}
        onClick={onToggle}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") onToggle();
        }}
        sx={{
          cursor: "pointer",
          p: { xs: 1.7, sm: 2 },
          borderRadius: "14px",
          border: `2px solid ${active ? COLORS.brand : "rgba(99,102,241,0.12)"}`,
          background: active ? "rgba(99,102,241,0.065)" : "rgba(255,255,255,0.94)",
          boxShadow: active ? "0 14px 34px rgba(99,102,241,0.1)" : "0 10px 24px rgba(15,23,42,0.035)",
          outline: "none",
          transition: "transform 0.22s ease, border-color 0.22s ease, box-shadow 0.22s ease",
          "&:hover": {
            borderColor: active ? COLORS.brand : "rgba(99,102,241,0.28)",
            transform: "translateX(3px)",
          },
          "&:focus-visible": {
            boxShadow: "0 0 0 4px rgba(99,102,241,0.16)",
          },
        }}
      >
        <Stack direction="row" spacing={1.7} alignItems="flex-start">
          <Box
            sx={{
              flexShrink: 0,
              width: 28,
              height: 28,
              borderRadius: "8px",
              border: `2px solid ${active ? COLORS.brand : "rgba(99,102,241,0.3)"}`,
              background: active ? COLORS.brand : "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mt: 0.15,
              transition: "background 0.2s ease, border-color 0.2s ease",
            }}
          >
            <AnimatePresence initial={false}>
              {active && (
                <motion.span
                  initial={{ scale: 0, rotate: -30 }}
                  animate={{ scale: 1, rotate: 0 }}
                  exit={{ scale: 0, rotate: 20 }}
                  transition={{ duration: 0.18 }}
                  style={{ display: "grid", placeItems: "center" }}
                >
                  <CheckRoundedIcon sx={{ color: "#fff", fontSize: 19 }} />
                </motion.span>
              )}
            </AnimatePresence>
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 0.45 }}>
              <Typography level="title-sm" sx={{ color: COLORS.dark, fontWeight: 800 }}>
                {layer.title}
              </Typography>

              <Chip
                size="sm"
                sx={{
                  "--Chip-minHeight": "20px",
                  "--Chip-paddingInline": "8px",
                  borderRadius: "7px",
                  color: layer.included ? "#047857" : COLORS.brand,
                  background: layer.included ? "rgba(16,185,129,0.12)" : "rgba(99,102,241,0.12)",
                  fontSize: "0.65rem",
                  fontWeight: 800,
                }}
              >
                {layer.included ? "В БАЗЕ" : layer.extra}
              </Chip>
            </Stack>

            <Typography level="body-sm" sx={{ color: "neutral.600", lineHeight: 1.5 }}>
              {layer.desc}
            </Typography>
          </Box>
        </Stack>
      </Box>
    </motion.div>
  );
}

function DrawingPreview({ active, view, onSelectView }) {
  return (
    <Box
      sx={{
        position: { xs: "relative", lg: "sticky" },
        top: { lg: 92 },
        minHeight: { xs: 430, sm: 630, lg: 690 },
        aspectRatio: { xs: "0.86 / 1", sm: "0.82 / 1", lg: "0.84 / 1" },
        width: { xs: "calc(100% - 16px)", sm: "100%" },
        maxWidth: { xs: 500, sm: 520, lg: "none" },
        mx: { xs: "auto", lg: 0 },
        boxSizing: "border-box",
        borderRadius: "22px",
        border: "1px solid rgba(99,102,241,0.16)",
        background: "#fff",
        boxShadow: "0 24px 70px rgba(40,45,120,0.12)",
        p: { xs: 2, sm: 2.5 },
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          inset: { xs: 14, sm: 18 },
          borderRadius: "14px",
          backgroundImage: `
            linear-gradient(to right, rgba(57,89,173,0.085) 1px, transparent 1px),
            linear-gradient(to bottom, rgba(57,89,173,0.085) 1px, transparent 1px)
          `,
          backgroundSize: "18px 18px",
        }}
      />

      <ViewTabs view={view} onSelectView={onSelectView} />
      <ScaleBadge />

      <Box
        sx={{
          position: "absolute",
          inset: { xs: "58px 18px 52px", sm: "62px 18px 58px" },
          zIndex: 1,
        }}
      >
        <AnimatePresence mode="wait">
          {view === VIEW_PLAN ? (
            <motion.div
              key="plan"
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.97 }}
              transition={{ duration: 0.32 }}
              style={{ width: "100%", height: "100%" }}
            >
              <svg viewBox="0 0 480 640" width="100%" height="100%" preserveAspectRatio="xMidYMid meet">
                <PlanDrawing activeLayers={active} />
              </svg>
            </motion.div>
          ) : (
            <motion.div
              key="facade"
              initial={{ opacity: 0, scale: 0.97, rotateY: 6 }}
              animate={{ opacity: 1, scale: 1, rotateY: 0 }}
              exit={{ opacity: 0, scale: 0.97, rotateY: -6 }}
              transition={{ duration: 0.36 }}
              style={{ width: "100%", height: "100%" }}
            >
              <svg viewBox="0 0 560 420" width="100%" height="100%" preserveAspectRatio="xMidYMid meet">
                <FacadeDrawing activeLayers={active} />
              </svg>
            </motion.div>
          )}
        </AnimatePresence>
      </Box>

      <Box
        sx={{
          position: "absolute",
          left: { xs: 16, sm: 24 },
          bottom: { xs: 15, sm: 20 },
          zIndex: 3,
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 1.4,
          py: 0.75,
          borderRadius: "8px",
          border: "1px solid rgba(99,102,241,0.18)",
          background: "rgba(255,255,255,0.92)",
          backdropFilter: "blur(10px)",
        }}
      >
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            background: "#10b981",
            boxShadow: "0 0 12px rgba(16,185,129,0.9)",
            "@keyframes includedLivePulse": {
              "0%, 100%": { opacity: 1, transform: "scale(1)" },
              "50%": { opacity: 0.45, transform: "scale(1.28)" },
            },
            animation: "includedLivePulse 2s ease-in-out infinite",
          }}
        />
        <Typography level="body-xs" sx={{ color: COLORS.dark, fontFamily: "monospace", fontSize: "0.68rem", fontWeight: 800 }}>
          LIVE PREVIEW
        </Typography>
      </Box>

    </Box>
  );
}

function ViewTabs({ view, onSelectView }) {
  return (
    <Box
      sx={{
        position: "absolute",
        top: { xs: 16, sm: 24 },
        left: { xs: 16, sm: 24 },
        zIndex: 3,
        display: "flex",
        gap: 0.5,
        p: 0.45,
        borderRadius: "10px",
        border: "1px solid rgba(99,102,241,0.16)",
        background: "rgba(255,255,255,0.78)",
        backdropFilter: "blur(10px)",
      }}
    >
      {[
        { key: VIEW_PLAN, label: "План" },
        { key: VIEW_FACADE, label: "Фасад" },
      ].map((tab) => (
        <Box
          key={tab.key}
          role="button"
          tabIndex={0}
          onClick={() => onSelectView(tab.key)}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") onSelectView(tab.key);
          }}
          sx={{
            px: 1.35,
            py: 0.55,
            borderRadius: "7px",
            cursor: "pointer",
            color: view === tab.key ? "#fff" : COLORS.brand,
            background: view === tab.key ? COLORS.brand : "transparent",
            fontFamily: "monospace",
            fontSize: "0.68rem",
            fontWeight: 800,
            outline: "none",
            transition: "background 0.2s ease, color 0.2s ease",
            "&:hover": {
              background: view === tab.key ? COLORS.brand : "rgba(99,102,241,0.1)",
            },
          }}
        >
          {tab.label}
        </Box>
      ))}
    </Box>
  );
}

function ScaleBadge() {
  return (
    <Box
      sx={{
        position: "absolute",
        top: { xs: 16, sm: 24 },
        right: { xs: 16, sm: 24 },
        zIndex: 3,
        px: 1.3,
        py: 0.55,
        borderRadius: "7px",
        border: "1px solid rgba(99,102,241,0.22)",
        background: "rgba(255,255,255,0.82)",
        color: COLORS.brand,
        fontFamily: "monospace",
        fontSize: "0.68rem",
        fontWeight: 800,
        backdropFilter: "blur(10px)",
      }}
    >
      M 1:50
    </Box>
  );
}

function PlanDrawing({ activeLayers }) {
  const has = (id) => activeLayers.has(id);

  return (
    <>
      <defs>
        <filter id="paperShadow" x="-10%" y="-10%" width="120%" height="120%">
          <feDropShadow dx="0" dy="8" stdDeviation="12" floodColor="#172554" floodOpacity="0.08" />
        </filter>
      </defs>

      <rect x="27" y="22" width="426" height="590" rx="5" fill="rgba(255,255,255,0.68)" filter="url(#paperShadow)" />

      {/* <text x="240" y="58" fill="#1f2937" fontSize="15" textAnchor="middle" fontWeight="800">
        Хлебница
      </text> */}

      {has("dimensions") && <PlanDimensions />}
      {has("walls") && <PlanWalls />}
      {has("heights") && <HeightMarks />}
      {has("plumbing") && <PlumbingLayer />}
      {has("electricity") && <ElectricLayer />}
      {has("photo") && <PhotoLayer />}
      {has("walls") && <RoomMarkers />}
    </>
  );
}

function PlanWalls() {
  const wall = COLORS.brand;
  const inner = "#5558e8";
  const fill = "rgba(99,102,241,0.026)";

  return (
    <motion.g initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.35 }}>
      <motion.path
        d="M 80 82 H 420 V 172 H 340 V 566 H 48 V 420 H 80 Z"
        fill={fill}
        stroke={wall}
        strokeWidth="9"
        strokeLinejoin="miter"
        initial={{ pathLength: 0 }}
        animate={{ pathLength: 1 }}
        transition={{ duration: 1.1, ease: "easeInOut" }}
      />

      <motion.line x1="286" y1="82" x2="286" y2="304" stroke={inner} strokeWidth="7" initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 0.7, delay: 0.2 }} />
      <motion.line x1="80" y1="304" x2="286" y2="304" stroke={inner} strokeWidth="7" initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 0.7, delay: 0.32 }} />
      <motion.line x1="286" y1="172" x2="420" y2="172" stroke={inner} strokeWidth="7" initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 0.55, delay: 0.44 }} />
      <motion.line x1="286" y1="250" x2="340" y2="250" stroke={inner} strokeWidth="7" initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 0.45, delay: 0.5 }} />
      <motion.line x1="80" y1="421" x2="340" y2="421" stroke={inner} strokeWidth="7" initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 0.7, delay: 0.58 }} />

      <DoorGap x1="153" y1="304" x2="197" y2="304" arc="M 153 304 A 44 44 0 0 1 197 304" />
      <DoorGap x1="286" y1="204" x2="286" y2="238" arc="M 286 238 A 34 34 0 0 0 252 204" />
      <DoorGap x1="312" y1="250" x2="338" y2="250" arc="M 312 250 A 27 27 0 0 0 338 223" />
      <DoorGap x1="246" y1="421" x2="290" y2="421" arc="M 246 430 A 44 44 0 0 0 290 386" />

      <Window x1={108} x2={164} y={86} />
      <Window x1={197} x2={259} y={86} />
      <Window x1={309} x2={356} y={86} />
      <Window x1={374} x2={410} y={86} />
      <Window vertical x={418} y1={118} y2={155} accent="#f59e0b" />

      <Column x={122} y={516} />
      <Column x={248} y={516} />
      <Column x={104} y={332} small />
    </motion.g>
  );
}

function DoorGap({ x1, y1, x2, y2, arc }) {
  return (
    <g>
      <line x1={x1} y1={y1} x2={x2} y2={y2} stroke="#fff" strokeWidth="10" strokeLinecap="round" />
      <path d={arc} stroke="#8b5cf6" strokeWidth="1.3" fill="none" strokeDasharray="4 3" opacity="0.72" />
    </g>
  );
}

function Window({ x1, x2, y, vertical = false, x, y1, y2, accent = "#2f80ed" }) {
  if (vertical) {
    return (
      <g>
        <line x1={x} y1={y1} x2={x} y2={y2} stroke="#fff" strokeWidth="10" />
        <line x1={x} y1={y1} x2={x} y2={y2} stroke={accent} strokeWidth="4" />
        <line x1={x + 4} y1={y1} x2={x + 4} y2={y2} stroke={accent === "#f59e0b" ? "#fbbf24" : "#93c5fd"} strokeWidth="1.2" />
      </g>
    );
  }

  return (
    <g>
      <line x1={x1} y1={y} x2={x2} y2={y} stroke="#fff" strokeWidth="10" />
      <line x1={x1} y1={y} x2={x2} y2={y} stroke={accent} strokeWidth="4" />
      <line x1={x1} y1={y - 4} x2={x2} y2={y - 4} stroke="#93c5fd" strokeWidth="1.2" />
    </g>
  );
}

function Column({ x, y, small = false }) {
  const size = small ? 18 : 24;

  return (
    <rect
      x={x - size / 2}
      y={y - size / 2}
      width={size}
      height={size}
      fill="rgba(99,102,241,0.22)"
      stroke="#5558e8"
      strokeWidth="1.5"
      opacity="0.96"
    />
  );
}

function PlanDimensions() {
  const blue = "#2f6fbf";
  const lightBlue = "#8ab6e7";

  return (
    <motion.g initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.35 }}>
      <DimensionLine x1={80} y1={604} x2={340} y2={604} label="11 365" labelX={210} labelY={622} color={blue} />
      <DimensionLine x1={48} y1={420} x2={48} y2={566} label="3 560" labelX={33} labelY={497} rotate color={blue} />
      <DimensionLine x1={64} y1={82} x2={64} y2={390} label="12 050" labelX={48} labelY={238} rotate color={blue} />
      <DimensionLine x1={80} y1={70} x2={286} y2={70} label="5 448" labelX={183} labelY={64} color={lightBlue} compact />
      <DimensionLine x1={286} y1={70} x2={420} y2={70} label="2 810" labelX={353} labelY={64} color={lightBlue} compact />
      <DimensionLine x1={80} y1={321} x2={286} y2={321} label="3 877" labelX={183} labelY={337} color={blue} dashed compact />
      <DimensionLine x1={356} y1={172} x2={356} y2={250} label="2 038" labelX={371} labelY={214} rotate color={lightBlue} dashed compact />
      <DimensionLine x1={48} y1={586} x2={80} y2={586} label="1 785" labelX={64} labelY={600} color={lightBlue} compact />
      <DimensionLine x1={80} y1={443} x2={340} y2={443} label="4 566" labelX={210} labelY={459} color={lightBlue} dashed compact />

      <DimensionText x={96} y={112} text="Hок = 1865" />
      <DimensionText x={212} y={112} text="Hв = 2360" />
      <DimensionText x={309} y={112} text="Hок = 1244" />
      <DimensionText x={102} y={476} text="H=2945" />
    </motion.g>
  );
}

function DimensionLine({ x1, y1, x2, y2, label, labelX, labelY, rotate = false, color, dashed = false, compact = false }) {
  return (
    <g opacity="0.92">
      <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={color} strokeWidth={compact ? 0.9 : 1.2} strokeDasharray={dashed ? "4 3" : undefined} />
      {rotate ? (
        <>
          <line x1={x1 - 5} y1={y1} x2={x1 + 5} y2={y1} stroke={color} strokeWidth="1.2" />
          <line x1={x2 - 5} y1={y2} x2={x2 + 5} y2={y2} stroke={color} strokeWidth="1.2" />
          <text x={labelX} y={labelY} fill={color} fontSize={compact ? 8 : 9} fontFamily="monospace" textAnchor="middle" fontWeight="800" transform={`rotate(-90 ${labelX} ${labelY})`}>
            {label}
          </text>
        </>
      ) : (
        <>
          <line x1={x1} y1={y1 - 5} x2={x1} y2={y1 + 5} stroke={color} strokeWidth="1.2" />
          <line x1={x2} y1={y2 - 5} x2={x2} y2={y2 + 5} stroke={color} strokeWidth="1.2" />
          <text x={labelX} y={labelY} fill={color} fontSize={compact ? 8 : 10} fontFamily="monospace" textAnchor="middle" fontWeight="800">
            {label}
          </text>
        </>
      )}
    </g>
  );
}

function DimensionText({ x, y, text }) {
  return (
    <text x={x} y={y} fill="#2f6fbf" fontSize="7" fontFamily="monospace" fontWeight="700">
      {text}
    </text>
  );
}

function HeightMarks() {
  const marks = [
    { x: 184, y: 205, label: "H=2763" },
    { x: 125, y: 372, label: "H=2945" },
    { x: 382, y: 134, label: "H=2038" },
    { x: 236, y: 520, label: "перепад 24" },
  ];

  return (
    <AnimatePresence>
      <motion.g initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}>
        {marks.map((mark, index) => (
          <motion.g key={mark.label} initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: index * 0.06, type: "spring", stiffness: 260 }}>
            <rect x={mark.x - 28} y={mark.y - 12} width="56" height="24" rx="4" fill="rgba(16,185,129,0.12)" stroke="#10b981" strokeWidth="1.1" />
            <text x={mark.x} y={mark.y + 4} fill="#047857" fontSize="7" fontFamily="monospace" textAnchor="middle" fontWeight="800">
              {mark.label}
            </text>
          </motion.g>
        ))}
      </motion.g>
    </AnimatePresence>
  );
}

function PlumbingLayer() {
  return (
    <AnimatePresence>
      <motion.g initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}>
        <rect x="296" y="202" width="30" height="24" rx="5" fill="rgba(6,182,212,0.12)" stroke="#0891b2" strokeWidth="1.8" />
        <circle cx="311" cy="213" r="5" fill="none" stroke="#0891b2" strokeWidth="1.2" />
        <rect x="301" y="266" width="28" height="14" rx="4" fill="rgba(6,182,212,0.12)" stroke="#0891b2" strokeWidth="1.8" />
        <circle cx="315" cy="273" r="2.2" fill="#0891b2" />
        <path d="M 112 292 C 112 330, 126 345, 126 386" stroke="#0891b2" strokeWidth="1.4" strokeDasharray="5 4" fill="none" />
        <circle cx="112" cy="292" r="4" fill="#06b6d4" opacity="0.35" stroke="#0891b2" />
        <text x="121" y="287" fill="#0891b2" fontSize="7" fontFamily="monospace" fontWeight="800">
          мокр. точка
        </text>
      </motion.g>
    </AnimatePresence>
  );
}

function ElectricLayer() {
  return (
    <AnimatePresence>
      <motion.g initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}>
        {SOCKETS.map((socket, index) => (
          <motion.g key={`${socket.x}-${socket.y}`} initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: index * 0.035, type: "spring", stiffness: 300 }}>
            <circle cx={socket.x} cy={socket.y} r="6" fill="#fef3c7" stroke="#d97706" strokeWidth="1.5" />
            <circle cx={socket.x - 2} cy={socket.y} r="0.9" fill="#b45309" />
            <circle cx={socket.x + 2} cy={socket.y} r="0.9" fill="#b45309" />
          </motion.g>
        ))}
        <rect x="86" y="92" width="17" height="22" rx="2" fill="#fef3c7" stroke="#d97706" strokeWidth="1.6" />
        <line x1="90" y1="99" x2="99" y2="99" stroke="#d97706" strokeWidth="1" />
        <line x1="90" y1="104" x2="99" y2="104" stroke="#d97706" strokeWidth="1" />
        <line x1="90" y1="109" x2="99" y2="109" stroke="#d97706" strokeWidth="1" />
      </motion.g>
    </AnimatePresence>
  );
}

function PhotoLayer() {
  return (
    <AnimatePresence>
      <motion.g initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}>
        {VIDEO_POINTS.map((point, index) => (
          <motion.g
            key={`${point.x}-${point.y}`}
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            transition={{ delay: index * 0.04, duration: 0.22 }}
          >
            <VideoCameraIcon x={point.x} y={point.y} angle={point.angle} />
          </motion.g>
        ))}
      </motion.g>
    </AnimatePresence>
  );
}

function VideoCameraIcon({ x, y, angle, compact = false }) {
  const scale = compact ? 0.58 : 0.72;

  return (
    <g transform={`translate(${x} ${y}) rotate(${angle}) scale(${scale})`}>
      <path d="M 12 -8 L 42 -21 L 42 21 L 12 8 Z" fill="rgba(99,102,241,0.12)" stroke="rgba(99,102,241,0.18)" strokeWidth="1" />
      <circle cx="0" cy="0" r="16" fill="rgba(224,45,20,0.14)" />
      <rect x="-14" y="-10" width="25" height="20" rx="5" fill="#e02d14" stroke="#b91c1c" strokeWidth="1.4" />
      <path d="M 10 -7 L 25 -14 L 25 14 L 10 7 Z" fill="#ff4a2f" stroke="#b91c1c" strokeWidth="1.2" strokeLinejoin="round" />
      <circle cx="-5" cy="0" r="4.5" fill="#fff" opacity="0.95" />
      <circle cx="-5" cy="0" r="2.2" fill="#312e81" opacity="0.75" />
      <rect x="-10" y="-14" width="12" height="4" rx="2" fill="#a5b4fc" />
    </g>
  );
}

function RoomMarkers() {
  const rooms = [
    { x: 206, y: 500, num: "1", area: "25,65 м²" },
    { x: 176, y: 203, num: "2", area: "37,63 м²" },
    { x: 318, y: 280, num: "3", area: "2,25 м²" },
    { x: 356, y: 128, num: "4", area: "4,15 м²" },
  ];

  return (
    <motion.g initial={{ opacity: 0 }} animate={{ opacity: 0.82 }} transition={{ delay: 0.65, duration: 0.3 }}>
      {rooms.map((room) => (
        <g key={room.num}>
          <circle cx={room.x} cy={room.y} r="14" fill="rgba(255,255,255,0.78)" stroke="#9aa8ff" strokeWidth="1.4" />
          <text x={room.x} y={room.y + 4} fill={COLORS.brand} fontSize="10" fontFamily="monospace" textAnchor="middle" fontWeight="900">
            {room.num}
          </text>
          <text x={room.x} y={room.y + 24} fill={COLORS.brand} fontSize="7" fontFamily="monospace" textAnchor="middle" fontWeight="700" opacity="0.74">
            {room.area}
          </text>
        </g>
      ))}
    </motion.g>
  );
}

function FacadeDrawing({ activeLayers }) {
  const has = (id) => activeLayers.has(id);
  const wallLeft = 60;
  const wallTop = 92;
  const wallWidth = 440;
  const wallHeight = 230;

  return (
    <>
      <rect x="24" y="34" width="512" height="350" rx="5" fill="rgba(255,255,255,0.7)" />
      <text x="280" y="64" fill="#1f2937" fontSize="15" textAnchor="middle" fontWeight="800">
        Фасад
      </text>

      <motion.g initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.35 }}>
        <rect x={wallLeft} y={wallTop} width={wallWidth} height={wallHeight} fill="rgba(99,102,241,0.025)" stroke={COLORS.brand} strokeWidth="6" />
        <line x1={wallLeft - 10} y1={wallTop - 8} x2={wallLeft + wallWidth + 10} y2={wallTop - 8} stroke="#5558e8" strokeWidth="4" />
        <rect x="205" y="54" width="150" height="24" rx="4" fill="rgba(99,102,241,0.08)" stroke={COLORS.brand} />
        

        {[
          { x: 82, w: 76 },
          { x: 174, w: 76 },
          { x: 266, w: 76 },
          { x: 408, w: 54 },
        ].map((item, index) => (
          <motion.g key={item.x} initial={{ y: 10, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: index * 0.07 }}>
            <rect x={item.x} y="126" width={item.w} height="156" rx="2" fill="rgba(47,128,237,0.08)" stroke="#2f80ed" strokeWidth="2.5" />
            <line x1={item.x} y1="204" x2={item.x + item.w} y2="204" stroke="#8ab6e7" />
            <line x1={item.x + item.w / 2} y1="126" x2={item.x + item.w / 2} y2="282" stroke="#8ab6e7" />
          </motion.g>
        ))}

        <rect x="354" y="164" width="38" height="118" rx="2" fill="rgba(99,102,241,0.06)" stroke={COLORS.brand} strokeWidth="2.3" />
        <circle cx="385" cy="224" r="2.7" fill={COLORS.brand} />
        <text x="373" y="298" fill={COLORS.brand} fontSize="7" fontFamily="monospace" textAnchor="middle" fontWeight="800">
          ВХОД
        </text>
      </motion.g>

      {has("dimensions") && (
        <motion.g initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <DimensionLine x1={wallLeft} y1={350} x2={wallLeft + wallWidth} y2={350} label="11 365" labelX={280} labelY={371} color="#2f6fbf" />
          <DimensionLine x1={40} y1={wallTop} x2={40} y2={wallTop + wallHeight} label="3 260" labelX={25} labelY={212} rotate color="#2f6fbf" />
        </motion.g>
      )}

      {has("heights") && (
        <motion.g initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <DimensionText x={438} y={118} text="H верх 3260" />
          <DimensionText x={438} y={142} text="H окна 1865" />
        </motion.g>
      )}

      {has("photo") && (
        <motion.g initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          {[120, 280, 440].map((x, index) => (
            <VideoCameraIcon key={x} x={x} y={333} angle={-90} compact />
          ))}
        </motion.g>
      )}
    </>
  );
}

function declOfNum(n, titles) {
  const cases = [2, 0, 1, 1, 1, 2];
  return titles[n % 100 > 4 && n % 100 < 20 ? 2 : cases[Math.min(n % 10, 5)]];
}
